import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { monkey } from 'baobab';
import assert from 'assert';
import lodashSortBy from 'lodash/fp/sortBy';
import lodashCompact from 'lodash/fp/compact';
import lodashPickBy from 'lodash/fp/pickBy';
import lodashIntersection from 'lodash/fp/intersection';
import moment from 'moment';
import { setIn } from 'formik';

import typeCheck from 'common/util/typeCheck';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import {
  HOUSEHOLD_APPLICATION_STATUS_LIST,
  HOUSEHOLD_APPLICATION_STATUS_PARENT_GROUP_TO_CHILDREN_MAPPING,
  HOUSEHOLD_LISTING_STATUS_ACTIVE,
  HOUSEHOLD_STATUS_ACTIVE,
} from 'common/householdConstants';

import {
  RENTLINX_LISTING_LEAD_STATUS_ACTIVE,
} from 'common/rentlinxConstants';

import { TOUR_SHOWING_STATUS_UNREAD } from 'common/tourConstants';
import { LEAD_STATUS_LEASE_SIGNED } from 'common/leadConstants';
import {
  USER_QUESTION_STATUS_SUBMITTED,
  USER_QUESTION_STATUS_ASKED,
  USER_QUESTION_STATUS_HISTORICAL_NOT_KNOWN,
} from 'common/userQuestionConstants';

import branch from 'client/core/branch';
import configureDecoratedComponent from 'client/helpers/configureDecoratedComponent';
import withApiState from 'client/helpers/withApiState';
import withInfiniteScroll from 'client/helpers/withInfiniteScroll';
import log from 'client/lib/log';

import {
  SR_API_ACTION_GET_DATA_TABLE_ITEMS,
} from 'api/actionTypes';

import updateSettings from 'client/actions/User/updateSettings';
import {
  JOB_ORDER_STATUS_COMPLETED,
  JOB_ORDER_STATUS_CREATED,
  JOB_STATUS_CANCELLED,
} from 'common/jobConstants';
import {
  DAYS_ON_MARKET_FILTER_1_30,
  DAYS_ON_MARKET_FILTER_30_60,
  DAYS_ON_MARKET_FILTER_60,
} from 'common/listingConstants';

const endOfTodayDateString = momentWithAustinTimezone().endOf('day');
const startOfTodayDateString = momentWithAustinTimezone().startOf('day');
const endOfTodayDate = momentWithAustinTimezone(endOfTodayDateString).toISOString();
const startOfTodayDate = momentWithAustinTimezone(startOfTodayDateString).toISOString();
const debug = log.debug('sunroom:dataTables');
const ALL_ITEMS_COUNT = 500000;
const withoutEmptyValues = lodashPickBy((item) => {
  if (typeof item === 'boolean') {
    return true;
  }
  return !!item;
});

function makeSortIdString(sort) {
  const sortIds = Object.keys(withoutEmptyValues(sort || {})).sort();
  return sortIds.length > 0
    ? sortIds.map((sortId) => `sort_${sortId}_${sort[sortId]}`).join('_')
    : 'sort_none';
}

function makeFuzzyIdString(fuzzy) {
  if (!fuzzy?.queryString || (fuzzy?.columns || []).length === 0) {
    return '';
  }
  return `fuzzy_${fuzzy.queryString}_cols_${fuzzy.columns.map((column) => column).join('_')}`;
}

function makeFilterIdString(filter) {
  const filterIds = Object.keys(withoutEmptyValues(filter || {})).sort();
  return filterIds.length > 0
    ? filterIds.map((filterId) => `filter_${filterId}_${JSON.stringify(filter[filterId])}`).join('_')
    : 'filter_none';
}

function makeCustomParamIdString(customParams) {
  const customParamIds = Object.keys(withoutEmptyValues(customParams || {})).sort();
  return customParamIds.length > 0
    ? customParamIds.map((customParamId) => `custom_param_${customParamId}_${JSON.stringify(customParams[customParamId])}`).join('_') // eslint-disable-line max-len
    : 'custom_param_none';
}

function getViewIdWithState({ rootViewId, viewSettings = {} }) {
  typeCheck('rootViewId::NonEmptyString', rootViewId);
  typeCheck('viewSettings::NonEmptyObject', viewSettings);

  const viewSettingsSortString = makeSortIdString(viewSettings.sort);
  const viewSettingsFuzzyString = makeFuzzyIdString(viewSettings.fuzzy);
  const viewSettingsFilterString = makeFilterIdString(viewSettings.filter);
  const viewSettingsCustomParamsString = makeCustomParamIdString(viewSettings.customParams);

  return lodashCompact([
    rootViewId,
    viewSettingsSortString,
    viewSettingsFuzzyString,
    viewSettingsFilterString,
    viewSettingsCustomParamsString,
  ]).join('_');
}

/**
 * Initializes a datable within a state object.
 *
 * @param  {Object} state                Baobab state object.
 * @param  {string} options.dataSource   Data source.
 * @param  {string} options.viewId       View ID (optional).
 * @param  {Object} options.filter       Filter object (optional).
 * @param  {Object} options.sort         Sort object (optional).
 * @param  {Object} options.fuzzy        Fuzzy object (optional).
 * @param  {Object} options.customParams Custom properties sent to the data table handler query generator.
 * @return {undefined}
 */
export function actionInitializeDataTableState({ state }, {
  dataSource,
  viewId,
  filter,
  sort,
  fuzzy,
  customParams,
  loadAllData,
}) {
  sort = withoutEmptyValues(sort);
  fuzzy = withoutEmptyValues(fuzzy);
  filter = withoutEmptyValues(filter);
  const sortId = makeSortIdString(sort);
  if (!state.exists(['dataTables', dataSource, 'items'])) {
    state.set(['dataTables', dataSource, 'items'], []);
  }

  if (!state.exists(['dataTables', dataSource, 'itemIdsBySort', sortId])) {
    state.set(['dataTables', dataSource, 'itemIdsBySort', sortId], []);
  }

  if (!state.exists(['dataTables', dataSource, 'views'])) {
    state.set(['dataTables', dataSource, 'views'], {});
  }

  if (!state.exists(['dataTables', dataSource, 'viewSettings'])) {
    state.set(['dataTables', dataSource, 'viewSettings'], {});
  }
  if (viewId && !state.exists(['dataTables', dataSource, 'views', viewId])) {
    state.set(['dataTables', dataSource, 'views', viewId], {
      filter,
      sort,
      fuzzy,
      customParams,
      loadedCount: 0,
      totalItemsCount: null,
    });
  }

  if (viewId && !state.exists(['dataTables', dataSource, 'views', viewId, '$items'])) {
    state.set(['dataTables', dataSource, 'views', viewId, '$items'], monkey({
      cursors: {
        items: ['dataTables', dataSource, 'items'],
        itemIdsInSortGroup: ['dataTables', dataSource, 'itemIdsBySort', sortId],
      },
      get({ items, itemIdsInSortGroup }) {
        if (!items) {
          return [];
        }
        const itemIdsExistInSortGroup = loadAllData
          ? {}
          : itemIdsInSortGroup.reduce((memo, id) => ({
            ...memo,
            [id]: true,
          }), {});

        let selectedItems = loadAllData
          ? items
          : items.filter(({ id }) => itemIdsExistInSortGroup[id]);

        const filterKeys = Object.keys(filter || {});

        if (filterKeys.length > 0) {
          selectedItems = selectedItems.filter((item) => {
            return filterKeys.every((filterField) => {
              // Ensure we're filtering by field without join table references.
              const filterFieldName = filterField.split('.').pop();
              if (Array.isArray(filter[filterField])) {
                return filter[filterField].some((filterValue) => item[filterFieldName] === filterValue);
              }
              return item[filterFieldName] === filter[filterField];
            });
          });
        }

        // WARNING(@d-oliveros): Sort by multiple properties is not supported yet.
        if (sort) {
          assert(Object.keys(sort).length <= 1, 'Sorting by multiple values is not yet supported.');
          const supportedSortValues = [
            'asc',
            'desc',
          ];
          assert(
            Object.keys(sort).every((sortFieldName) => supportedSortValues.includes(sort[sortFieldName])),
            `Sorting value in ${JSON.stringify(sort)} not supported. Available values: ${supportedSortValues}`
          );

          const sortByGroups = Object.keys(sort).reduce((acc, sortFieldName) => [
            ...acc,
            {
              fieldPath: sortFieldName,
              fieldName: sortFieldName.split('.').pop(),
              direction: sort[sortFieldName],
            },
          ], []);

          selectedItems = lodashSortBy(sortByGroups.map(({ fieldName }) => fieldName), selectedItems);

          if (sortByGroups[0]?.direction === 'desc') {
            const firstNull = selectedItems.findIndex((element) => {
              return element[sortByGroups[0]?.fieldName] === null;
            });
            if (firstNull > -1) {
              const nullsArray = selectedItems.slice(firstNull);
              const valuesArray = selectedItems.slice(0, firstNull);
              valuesArray.reverse();
              selectedItems = [...valuesArray, ...nullsArray];
            }
            else {
              selectedItems.reverse();
            }
          }
        }

        if (fuzzy?.queryString && (fuzzy?.columns || []).length > 0) {
          const fuzzyQueryStringLowerCase = fuzzy.queryString.toLowerCase();

          selectedItems = selectedItems.filter((item) => {
            return fuzzy.columns.some((column) => {
              return typeof item[column] === 'string'
                ? item[column].toLowerCase().includes(fuzzyQueryStringLowerCase)
                : false;
            });
          });
        }

        // HACK(@d-oliveros): When sending "customParams" to data table handlers, we need to run custom code to manually filter using these customParams.
        // TODO(@d-oliveros): Figure out a way to not have to use customParams.
        if (customParams) {

          // DATA_TABLE_SOURCE_HOUSEHOLD_DASHBOARD
          const householdDashboardCustomParams = customParams.householdDashboard || {};
          if (householdDashboardCustomParams.userId) {
            selectedItems = selectedItems.filter((item) => {
              return item.users.some(({ id }) => id === householdDashboardCustomParams.userId);
            });
          }
          if (householdDashboardCustomParams.applicationStatus) {
            const applicationStatusFunnelIndex = (
              HOUSEHOLD_APPLICATION_STATUS_LIST.indexOf(householdDashboardCustomParams.applicationStatus)
            );
            const applicationStatusesToExclude = (
              HOUSEHOLD_APPLICATION_STATUS_LIST.slice(0, applicationStatusFunnelIndex)
            );
            const applicationStatusChildrenGroup = HOUSEHOLD_APPLICATION_STATUS_PARENT_GROUP_TO_CHILDREN_MAPPING[householdDashboardCustomParams.applicationStatus]; // eslint-disable-line max-len
            selectedItems = selectedItems.filter((item) => {
              const hasApplicationStatusMatch = item.listingApplications.some(({ householdListingStatus, applications }) => { // eslint-disable-line max-len
                if (householdListingStatus !== HOUSEHOLD_LISTING_STATUS_ACTIVE) {
                  return false;
                }
                return applications.some(({ status }) => {
                  return applicationStatusChildrenGroup.includes(status);
                });
              });
              const isWeakestLink = applicationStatusesToExclude.length === 0 ? true : (
                item.listingApplications.every(({ householdListingStatus, applications }) => {
                  if (householdListingStatus !== HOUSEHOLD_LISTING_STATUS_ACTIVE) {
                    return true;
                  }
                  return applications.every(({ status }) => !applicationStatusesToExclude.includes(status));
                })
              );
              return hasApplicationStatusMatch && isWeakestLink;
            });
          }

          // DATA_TABLE_SOURCE_HOUSEHOLD_DASHBOARD
          if (householdDashboardCustomParams.moveInDateRanges) {
            selectedItems = selectedItems.filter(({ renterMoveInDate }) => (
              householdDashboardCustomParams.moveInDateRanges.some((moveInDateRange) => {
                const initialMoveInDate = momentWithAustinTimezone(moveInDateRange[0]);
                const endMoveInDate = momentWithAustinTimezone(moveInDateRange[1]);
                return (
                  initialMoveInDate.isSameOrBefore(renterMoveInDate)
                  && endMoveInDate.isSameOrAfter(renterMoveInDate)
                );
              })
            ));
          }

          // DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN
          const householdOverviewCustomParams = customParams.householdOverview || {};
          const isListingManagementCompanyArray = Array
            .isArray(householdOverviewCustomParams.listingManagementCompany);
          if (householdOverviewCustomParams.moveInDateRanges) {
            selectedItems = selectedItems.filter(({ renterMoveInDate }) => (
              householdOverviewCustomParams.moveInDateRanges.some((moveInDateRange) => {
                const initialMoveInDate = momentWithAustinTimezone(moveInDateRange[0]);
                const endMoveInDate = momentWithAustinTimezone(moveInDateRange[1]);
                return (
                  initialMoveInDate.isSameOrBefore(renterMoveInDate)
                  && endMoveInDate.isSameOrAfter(renterMoveInDate)
                );
              })
            ));
          }
          if (householdOverviewCustomParams.listingManagementCompany !== undefined) {
            selectedItems = selectedItems.filter(({ listingApplications }) => {
              return listingApplications.some(({ listing }) => {
                return isListingManagementCompanyArray
                  ? householdOverviewCustomParams.listingManagementCompany.includes(listing.listingManagementCompany) // eslint-disable-line max-len
                  : listing.listingManagementCompany === householdOverviewCustomParams.listingManagementCompany; // eslint-disable-line max-len
              });
            });
          }
          if (householdOverviewCustomParams.accountManagerUserId !== undefined) {
            selectedItems = selectedItems.filter(({ listingApplications }) => {
              return listingApplications.some(({ listing }) => {
                return householdOverviewCustomParams.accountManagerUserId
                  .includes(listing.partnerAccountManagerUserId);
              });
            });
          }
          if (householdOverviewCustomParams.includeHouseholdApplicationStatusList) {
            selectedItems = selectedItems.filter(({ listingApplications }) => {
              return listingApplications.some(({ applications, listing }) => {
                const applicationStatusOk = applications.some(({ status }) => {
                  return householdOverviewCustomParams.includeHouseholdApplicationStatusList.includes(status);
                });
                const listingOk = isListingManagementCompanyArray
                  ? householdOverviewCustomParams.listingManagementCompany.includes(listing.listingManagementCompany) // eslint-disable-line max-len
                  : listing.listingManagementCompany === householdOverviewCustomParams.listingManagementCompany; // eslint-disable-line max-len

                return applicationStatusOk && listingOk;
              });
            });
          }
          if (householdOverviewCustomParams.excludeHouseholdApplicationStatusList) {
            selectedItems = selectedItems.filter(({ listingApplications }) => {
              return !listingApplications.some(({ applications, listing }) => {
                const applicationStatusOk = applications.some(({ status }) => {
                  return householdOverviewCustomParams.excludeHouseholdApplicationStatusList.includes(status);
                });
                const listingOk = isListingManagementCompanyArray
                  ? householdOverviewCustomParams.listingManagementCompany.includes(listing.listingManagementCompany) // eslint-disable-line max-len
                  : listing.listingManagementCompany === householdOverviewCustomParams.listingManagementCompany; // eslint-disable-line max-len

                return applicationStatusOk && listingOk;
              });
            });
          }

          // DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_APPLICATION_COLUMN
          if (customParams.householdKanbanApplicationColumn) {
            const {
              listingManagementCompany,
              listingServices,
              includeHouseholdApplicationStatusList,
              excludeHouseholdApplicationStatusList,
              householdListingStatus,
              accountManagerUserId,
              leasingManagerUserId,
            } = customParams.householdKanbanApplicationColumn;

            if (includeHouseholdApplicationStatusList) {
              const isListingManagementCompanyArray = Array.isArray(listingManagementCompany);

              selectedItems = selectedItems.filter(({ listingApplications, status }) => {
                const includeAppsOk = listingApplications.some(({ applications, listing, status }) => {
                  return applications.some((app) => {
                    const applicationStatusOk = includeHouseholdApplicationStatusList.includes(app.status);

                    let listingOk = true;
                    if (listingManagementCompany !== undefined) {
                      listingOk = isListingManagementCompanyArray
                        ? listingManagementCompany.includes(listing.listingManagementCompany)
                        : listing.listingManagementCompany === listingManagementCompany;
                    }

                    let accountManagerOk = true;
                    if (accountManagerUserId !== undefined) {
                      accountManagerOk = listingApplications.some(({ listing }) => {
                        return accountManagerUserId.includes(listing.partnerAccountManagerUserId);
                      });
                    }

                    if (leasingManagerUserId) {
                      accountManagerOk = listingApplications.some(({ listing }) => {
                        return leasingManagerUserId.includes(listing.partnerLeasingOperationsManagerUserId);
                      });
                    }

                    return (
                      applicationStatusOk
                      && listingOk
                      && accountManagerOk
                      && householdListingStatus === status
                    );
                  });
                });

                const excludeappsOk = excludeHouseholdApplicationStatusList && excludeHouseholdApplicationStatusList.length // eslint-disable-line max-len
                  ? !listingApplications.some(({ applications }) => {
                    return applications.some(({ status }) => {
                      return excludeHouseholdApplicationStatusList.includes(status);
                    });
                  })
                  : true;

                const statusOk = householdListingStatus === HOUSEHOLD_LISTING_STATUS_ACTIVE
                  ? status === HOUSEHOLD_STATUS_ACTIVE
                  : true;

                const servicesOk = listingServices?.length
                  ? listingApplications.some(({ listing }) => {
                    return lodashIntersection(listing.services, listingServices).length > 0;
                  })
                  : true;

                return includeAppsOk && excludeappsOk && statusOk && servicesOk;
              });
            }
          }

          // DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_TOURING
          const householdKanbanTouringCustomParams = customParams.householdKanbanTouring || {};
          if (householdKanbanTouringCustomParams.listingManagementCompany !== undefined) {
            const isListingManagementCompanyArray = Array
              .isArray(householdKanbanTouringCustomParams.listingManagementCompany);
            selectedItems = selectedItems.filter(({ listings }) => {
              return listings.some(({ listingManagementCompany }) => {
                return isListingManagementCompanyArray
                  ? householdKanbanTouringCustomParams.listingManagementCompany.includes(listingManagementCompany) // eslint-disable-line max-len
                  : listingManagementCompany === householdKanbanTouringCustomParams.listingManagementCompany; // eslint-disable-line max-len
              });
            });
          }
          if (householdKanbanTouringCustomParams.listingServices !== undefined) {
            selectedItems = selectedItems.filter(({ listings }) => {
              return listings.some(({ services }) => {
                return lodashIntersection(services, householdKanbanTouringCustomParams.listingServices).length > 0; // eslint-disable-line max-len
              });
            });
          }
          if (householdKanbanTouringCustomParams.leasingManagerUserId !== undefined) {
            selectedItems = selectedItems.filter(({ listings }) => {
              return listings.some(({ partnerLeasingOperationsManagerUserId }) => {
                return householdKanbanTouringCustomParams.leasingManagerUserId
                  .includes(partnerLeasingOperationsManagerUserId);
              });
            });
          }

          // DATA_TABLE_SOURCE_AGENT_LEADS
          const agentLeadsCustomParams = customParams.agentLeads || {};
          if (agentLeadsCustomParams.searchText) {
            const searchText = agentLeadsCustomParams.searchText.toLowerCase();
            selectedItems = selectedItems.filter((item) => {
              return (
                `${item.renter.name.toLowerCase()}`.includes(searchText)
                || `${item.renter.phone}`.includes(searchText)
                || `${item.apartmentLocatorNotes}`.toLowerCase().includes(searchText)
              );
            });
          }
          if (agentLeadsCustomParams.userMoveInDateRange) {
            typeCheck('userMoveInDateRange::NonEmptyArray', agentLeadsCustomParams.userMoveInDateRange);
            const isLeaseSignedStatus = agentLeadsCustomParams.status === LEAD_STATUS_LEASE_SIGNED;
            const getFieldDate = (item) => isLeaseSignedStatus
              ? item.lease.startDate
              : item.renter.moveInDate;

            const { userMoveInDateRange } = agentLeadsCustomParams;
            const initialDate = momentWithAustinTimezone(userMoveInDateRange[0]);
            const endDate = userMoveInDateRange[1] && momentWithAustinTimezone(userMoveInDateRange[1]);
            selectedItems = selectedItems
              .filter((item) => initialDate.isSameOrBefore(getFieldDate(item)))
              .filter((item) => endDate ? endDate.isSameOrAfter(getFieldDate(item)) : true);
          }

          // DATA_TABLE_SOURCE_ADMIN_TOURS
          const adminToursDashboardCustomParams = customParams.tourDashboard || {};
          if (adminToursDashboardCustomParams.unread) {
            selectedItems = selectedItems.filter((item) => {
              return item.showings.some((showing) => {
                return showing.status === TOUR_SHOWING_STATUS_UNREAD;
              });
            });
          }
          if (adminToursDashboardCustomParams.unaskedQuestion) {
            selectedItems = selectedItems.filter((item) => {
              const startOfTourDateTresholdString = moment()
                .subtract(3, 'week').toISOString();
              if (moment(item.startDate).isBefore(startOfTourDateTresholdString)) {
                return false;
              }
              return item.showings.some((showing) => {
                return showing.renterQuestions
                  && showing.renterQuestions.length > 0
                  && showing.renterQuestions.some((question) => {
                    return [
                      USER_QUESTION_STATUS_HISTORICAL_NOT_KNOWN,
                      USER_QUESTION_STATUS_SUBMITTED,
                    ].includes(question.status);
                  });
              });
            });
          }
          if (adminToursDashboardCustomParams.unansweredQuestion) {
            selectedItems = selectedItems.filter((item) => {
              const startOfTourDateTresholdString = moment()
                .subtract(3, 'week').toISOString();
              if (moment(item.startDate).isBefore(startOfTourDateTresholdString)) {
                return false;
              }
              return item.showings.some((showing) => {
                return showing.renterQuestions
                  && showing.renterQuestions.length > 0
                  && showing.renterQuestions.some((question) => {
                    return question.status === USER_QUESTION_STATUS_ASKED;
                  });
              });
            });
          }
          if (adminToursDashboardCustomParams.unassigned) {
            selectedItems = selectedItems.filter((item) => {
              return (
                item.startDate > startOfTodayDate
                && item.startDate < endOfTodayDate
              );
            });
          }
          if (adminToursDashboardCustomParams.marketId) {
            selectedItems = selectedItems.filter((item) => {
              return (
                item.market
                && item.market.id === adminToursDashboardCustomParams.marketId
              );
            });
          }
          if (adminToursDashboardCustomParams.listingManagementCompany !== undefined) {
            const isListingManagementCompanyArray = Array
              .isArray(adminToursDashboardCustomParams.listingManagementCompany);
            selectedItems = selectedItems.filter(({ listings }) => {
              return listings.some((listing) => {
                return isListingManagementCompanyArray
                  ? adminToursDashboardCustomParams.listingManagementCompany.includes(listing.listingManagementCompany) // eslint-disable-line max-len
                  : listing.listingManagementCompany === adminToursDashboardCustomParams.listingManagementCompany; // eslint-disable-line max-len
              });
            });
          }

          // DATA_TABLE_SOURCE_LEASES
          const adminLeasesDashboardCustomParams = customParams.leasesDashboard || {};
          if (adminLeasesDashboardCustomParams.pastStartDate) {
            const nowMoment = moment();
            selectedItems = selectedItems.filter(({ startDate }) => {
              return nowMoment.isAfter(startDate);
            });
          }

          // DATA_TABLE_SOURCE_PARTNER_LISTING_TOUR_REVIEWS
          const partnerListingTourReviewsCustomParams = customParams.partnerListingTourReviews || {};
          if (partnerListingTourReviewsCustomParams.sinceDate) {
            selectedItems = selectedItems.filter(({ startDate }) => {
              return moment(startDate).isAfter(partnerListingTourReviewsCustomParams.sinceDate);
            });
          }
          if (partnerListingTourReviewsCustomParams.listingId) {
            selectedItems = selectedItems.filter(({ tourListing }) => {
              return tourListing.listingId === partnerListingTourReviewsCustomParams.listingId;
            });
          }
          if (partnerListingTourReviewsCustomParams.apartmentBuildingId) {
            const isApartmentBuildingIdArray = Array
              .isArray(partnerListingTourReviewsCustomParams.apartmentBuildingId);
            selectedItems = selectedItems.filter(({ listing }) => {
              return isApartmentBuildingIdArray
                ? partnerListingTourReviewsCustomParams.apartmentBuildingId.includes(listing.apartmentBuildingId) // eslint-disable-line max-len
                : listing.apartmentBuildingId === partnerListingTourReviewsCustomParams.apartmentBuildingId; // eslint-disable-line max-len
            });
          }
          // DATA_TABLE_SOURCE_PARTNER_APARTMENTS
          const partnerApartmentsCustomParams = customParams.partnerApartments || {};
          if (partnerApartmentsCustomParams.onlyActiveListings) {
            selectedItems = selectedItems.filter((apartment) => apartment.activeListings > 0);
          }
          if (typeof partnerApartmentsCustomParams.appInProgress === 'boolean') {
            selectedItems = selectedItems.filter(({ appInProgress }) => {
              return appInProgress === partnerApartmentsCustomParams.appInProgress;
            });
          }

          // DATA_TABLE_SOURCE_PARTNER_LISTINGS
          const partnerListingsCustomParams = customParams.partnerListings || {};
          if (typeof partnerListingsCustomParams.appInProgress === 'boolean') {
            selectedItems = selectedItems.filter(({ appInProgress }) => {
              return appInProgress === partnerListingsCustomParams.appInProgress;
            });
          }
          if (typeof partnerListingsCustomParams.serviceType === 'string') {
            selectedItems = selectedItems.filter(({ services }) => {
              return services.includes(partnerListingsCustomParams.serviceType);
            });
          }
          if (partnerListingsCustomParams.services) {
            selectedItems = selectedItems.filter(({ services }) => {
              return lodashIntersection(partnerListingsCustomParams.services, services).length > 0;
            });
          }

          if (partnerListingsCustomParams.marketIds) {
            selectedItems = selectedItems.filter(({ marketId }) => (
              partnerListingsCustomParams.marketIds.includes(marketId)
            )
            );
          }

          // DATA_TABLE_SOURCE_ADMIN_LISTINGS_LIST
          const adminListingsListCustomParams = customParams.adminListingList || {};
          if (adminListingsListCustomParams.listingAvailableDates) {
            selectedItems = selectedItems.filter(({ availableDate }) => {
              return adminListingsListCustomParams.listingAvailableDates.some((month) => {
                const initialAvailableDate = momentWithAustinTimezone(month[0]);
                const endAvailableDate = momentWithAustinTimezone(month[1]);
                return (
                  initialAvailableDate.isSameOrBefore(availableDate)
                  && endAvailableDate.isSameOrAfter(availableDate)
                );
              });
            });
          }

          if (adminListingsListCustomParams.listingServices) {
            selectedItems = selectedItems.filter(({ services }) => {
              return adminListingsListCustomParams.listingServices
                .some((listingService) => services.includes(listingService));
            });
          }

          // DATA_TABLE_SOURCE_PARTNER_STATS_TOURS
          const partnerStatsTours = customParams.partnerStatsTours || {};

          if (partnerStatsTours.listingManagementCompany) {
            selectedItems = selectedItems
              .filter(({ listing }) => (
                listing.listingManagementCompany === partnerStatsTours.listingManagementCompany
              ));
          }
          if (partnerStatsTours.listingId) {
            selectedItems = selectedItems
              .filter((item) => item.listing.id === partnerStatsTours.listingId);
          }
          if (partnerStatsTours.apartmentId) {
            selectedItems = selectedItems
              .filter((item) => item.apartment.id === partnerStatsTours.apartmentId);
          }
          if (partnerStatsTours.sinceDate) {
            const sinceDate = momentWithAustinTimezone(partnerStatsTours.sinceDate);
            selectedItems = selectedItems
              .filter((item) => sinceDate.isSameOrBefore(item.startDate));
          }
          if (partnerStatsTours.nonRentlinxLeads) {
            selectedItems = selectedItems
              .filter((item) => !item.isRentlinxLead);
          }
          if (partnerStatsTours.tourStatus) {
            const tourStatusArray = Array.isArray(partnerStatsTours.tourStatus)
              ? partnerStatsTours.tourStatus
              : [partnerStatsTours.tourStatus];

            selectedItems = selectedItems
              .filter((item) => tourStatusArray.includes(item.status));
          }

          // DATA_TABLE_SOURCE_PARTNER_STATS_RENTLINX_LEADS
          const partnerStatsRentlinxLeads = customParams.partnerStatsRentlinxLeads || {};
          if (partnerStatsRentlinxLeads.listingManagementCompany) {
            selectedItems = selectedItems
              .filter(({ listing, partner }) => (
                listing.listingManagementCompany === partnerStatsRentlinxLeads.listingManagementCompany
                || partner.managementCompanyName === partnerStatsRentlinxLeads.listingManagementCompany
              ));
          }
          if (partnerStatsRentlinxLeads.listingId) {
            selectedItems = selectedItems
              .filter((item) => item.listing.id === partnerStatsRentlinxLeads.listingId);
          }
          if (partnerStatsRentlinxLeads.apartmentId) {
            selectedItems = selectedItems
              .filter((item) => item.apartment.id === partnerStatsRentlinxLeads.apartmentId);
          }
          if (partnerStatsRentlinxLeads.sinceDate) {
            const sinceDate = momentWithAustinTimezone(partnerStatsRentlinxLeads.sinceDate);
            selectedItems = selectedItems
              .filter((item) => sinceDate.isSameOrBefore(item.createdDate));
          }

          // DATA_TABLE_SOURCE_PARTNER_STATS_SUPRA_SHOWINGS
          const partnerStatsSupraShowings = customParams.partnerStatsSupraShowings || {};
          if (partnerStatsSupraShowings.listingManagementCompany) {
            selectedItems = selectedItems
              .filter(({ listing }) => (
                listing.listingManagementCompany === partnerStatsSupraShowings.listingManagementCompany
              ));
          }
          if (partnerStatsSupraShowings.listingId) {
            selectedItems = selectedItems
              .filter((item) => item.listing.id === partnerStatsSupraShowings.listingId);
          }
          if (partnerStatsSupraShowings.apartmentId) {
            selectedItems = selectedItems
              .filter((item) => item.apartment.id === partnerStatsSupraShowings.apartmentId);
          }
          if (partnerStatsSupraShowings.sinceDate) {
            const sinceDate = momentWithAustinTimezone(partnerStatsSupraShowings.sinceDate);
            selectedItems = selectedItems
              .filter((item) => sinceDate.isSameOrBefore(item.inDate));
          }

          // DATA_TABLE_SOURCE_HOUSEHOLD_LISTINGS
          const householdListings = customParams.householdListings || {};
          if (householdListings.householdListingId) {
            selectedItems = selectedItems
              .filter(({ householdListing }) => (
                householdListing.id === householdListings.householdListingId
              ));
          }

          // DATA_TABLE_SOURCE_LISTING_LEAD_HEALTH
          const listingLeadHealth = customParams.listingLeadHealth || {};
          if (listingLeadHealth.marketId) {
            selectedItems = selectedItems
              .filter(({ market }) => listingLeadHealth.marketId.includes(market.id));
          }

          if (listingLeadHealth.partnerId) {
            selectedItems = selectedItems
              .filter(({ partner }) => listingLeadHealth.partnerId.includes(partner.id));
          }

          if (listingLeadHealth.accountManagerId) {
            selectedItems = selectedItems
              .filter(({ partner }) => listingLeadHealth.accountManagerId.includes(partner.accountManagerUserId)); // eslint-disable-line max-len
          }

          // DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS
          const statusToFilter = [
            JOB_STATUS_CANCELLED,
          ];
          const partnerListings = customParams.partnerListings || {};
          if (partnerListings.serviceType) {
            selectedItems = selectedItems
              .filter(({ services }) => (
                services.includes(partnerListings.serviceType)
              ));
          }
          if (partnerListings.uninstallJobs) {
            selectedItems = selectedItems
              .filter(({
                takedownJob,
              }) => (
                !!takedownJob
              ))
              .filter(({
                yardSignTakedownJob,
                comboLockTakedownJob,
                sunlockTakedownJob,
                selfShowingHardwareTakedownJob,
              }) =>
                !statusToFilter.includes(yardSignTakedownJob?.jobOrderStatus)
                && !statusToFilter.includes(comboLockTakedownJob?.jobOrderStatus)
                && !statusToFilter.includes(sunlockTakedownJob?.jobOrderStatus)
                && !statusToFilter.includes(selfShowingHardwareTakedownJob?.jobOrderStatus))
              .sort((a, b) => {
                if (a.takedownJob.jobOrderStatus === JOB_ORDER_STATUS_CREATED
                  && b.takedownJob.jobOrderStatus === JOB_ORDER_STATUS_COMPLETED
                ) {
                  return -1;
                } return 0;
              });
          }
          if (partnerListings.excludeUninstallJobs) {
            selectedItems = selectedItems
              .filter(({
                takedownJob,
              }) => (
                !takedownJob
              ));
          }
          const marketingPartnerListings = customParams.marketingPartnerListings || {};
          if (marketingPartnerListings.marketIds) {
            selectedItems = selectedItems
              .filter((element) => (marketingPartnerListings.marketIds.includes(element.marketId)));
          }

          // DATA_TABLE_SOURCE_PARTNER_HOUSEHOLD_PENDING_APPROVALS
          const partnerApprovals = customParams.partnerApprovals || {};
          if (partnerApprovals.marketIds) {
            selectedItems = selectedItems
              .filter((element) => (partnerApprovals.marketIds.includes(element.marketId)));
          }

          // DATA_TABLE_SOURCE_PARTNER_HOUSEHOLD_MOVE_INS
          const partnerHouseholdMoveInsCustomParams = customParams.partnerHouseholdMoveInsCustomParams || {};
          if (partnerHouseholdMoveInsCustomParams.marketIds) {
            selectedItems = selectedItems
              .filter(
                (element) => (partnerHouseholdMoveInsCustomParams.marketIds.includes(element.marketId))
              );
          }

          // DATA_TABLE_SOURCE_RENTLINX_LEADS
          const householdKanbanRentlinxLeads = customParams.householdKanbanRentlinxLeads;
          if (householdKanbanRentlinxLeads) {
            const isListingManagementCompanyArray = Array.isArray(householdKanbanRentlinxLeads.listingManagementCompany); // eslint-disable-line max-len

            selectedItems = selectedItems.filter(({ listings }) => {
              return listings.some((listing) => {
                let okCreatedDate = true;
                let okManagementCompany = true;
                let okListingLeadStatus = true;
                let okListingServices = true;

                if (typeof householdKanbanRentlinxLeads.onlyActive === 'boolean') {
                  if (householdKanbanRentlinxLeads.onlyActive) {
                    okListingLeadStatus = listings.some(({ rentlinxListingLeadStatus }) => {
                      return rentlinxListingLeadStatus === RENTLINX_LISTING_LEAD_STATUS_ACTIVE;
                    });
                  }
                  else {
                    okListingLeadStatus = !listings.some(({ rentlinxListingLeadStatus }) => {
                      return rentlinxListingLeadStatus === RENTLINX_LISTING_LEAD_STATUS_ACTIVE;
                    });
                  }
                }

                if (householdKanbanRentlinxLeads.createdDateRanges) {
                  okCreatedDate = householdKanbanRentlinxLeads.createdDateRanges.some((createdDateRange) => { // eslint-disable-line max-len
                    const initialMoveInDate = momentWithAustinTimezone(createdDateRange[0]);
                    const endMoveInDate = momentWithAustinTimezone(createdDateRange[1]);
                    return (
                      initialMoveInDate.isSameOrBefore(moment(listing._createdDate))
                      && endMoveInDate.isSameOrAfter(moment(listing._createdDate))
                    );
                  });
                }

                if (householdKanbanRentlinxLeads.listingManagementCompany !== undefined) {
                  okManagementCompany = isListingManagementCompanyArray
                    ? householdKanbanRentlinxLeads.listingManagementCompany.includes(listing.listingManagementCompany) // eslint-disable-line max-len
                    : listing.listingManagementCompany === householdKanbanRentlinxLeads.listingManagementCompany; // eslint-disable-line max-len
                }

                if (householdKanbanRentlinxLeads.listingManagementCompany !== undefined) {
                  okManagementCompany = isListingManagementCompanyArray
                    ? householdKanbanRentlinxLeads.listingManagementCompany.includes(listing.listingManagementCompany) // eslint-disable-line max-len
                    : listing.listingManagementCompany === householdKanbanRentlinxLeads.listingManagementCompany; // eslint-disable-line max-len
                }

                if (householdKanbanRentlinxLeads.listingServices) {
                  okListingServices = lodashIntersection(listing.services, householdKanbanRentlinxLeads.listingServices).length > 0; // eslint-disable-line max-len
                }

                return (
                  okCreatedDate && okManagementCompany && okListingLeadStatus && okListingServices
                );
              });
            });
          }

          // DATA_TABLE_SOURCE_SUNSETUP_COMPLETED_SETUPS
          if (
            Array.isArray(customParams?.completedSetups?.daysOnMarket)
            && customParams.completedSetups.daysOnMarket.length
          ) {
            selectedItems = selectedItems
              .filter((item) => {
                let isValid = false;

                for (const daysOnMarketFilter of customParams.completedSetups.daysOnMarket) {
                  if (daysOnMarketFilter === DAYS_ON_MARKET_FILTER_1_30 && !isValid) {
                    isValid = item.daysOnMarket <= 30;
                  }
                  else if (daysOnMarketFilter === DAYS_ON_MARKET_FILTER_30_60 && !isValid) {
                    isValid = item.daysOnMarket > 30 && item.daysOnMarket <= 60;
                  }
                  else if (daysOnMarketFilter === DAYS_ON_MARKET_FILTER_60 && !isValid) {
                    isValid = item.daysOnMarket > 60;
                  }
                  if (isValid) {
                    break;
                  }
                }

                return isValid;
              });
          }
        }

        return selectedItems;
      },
    }));

    // HACK(@d-oliveros):
    //  baobab-react is not emitting an update event after creating a new monkey.
    //  Faking a value update to trigger a baobab-react component update.
    const currentItems = state.get(['dataTables', dataSource, 'items']);
    state.set(['dataTables', dataSource, 'items'], [...(currentItems || [])]);

    // NOTE(@d-oliveros): Only commit if a view object has been created, as it may already have listeners waiting for data.
    state.commit();
  }
}

/**
 * Initializes the settings for a datable view.
 *
 * @param  {Object}  state                Baobab state object.
 * @param  {string}  options.dataSource   Data source.
 * @param  {string}  options.rootViewId   View ID.
 * @param  {Object}  options.viewSettings Filter, sort and other view settings.
 * @param  {boolean} options.loadAllData  If true, the data will not be segmented by child view IDs as all the data would be initially loaded.
 * @return {undefined}
 */
export function actionInitializeDataTableUIControlsState({ state }, {
  dataSource,
  rootViewId,
  viewSettings: _viewSettings,
}) {
  const viewSettings = { ...(_viewSettings || {}) };

  viewSettings.sort = withoutEmptyValues(viewSettings.sort || {});
  viewSettings.fuzzy = withoutEmptyValues(viewSettings.fuzzy || {});
  viewSettings.filter = withoutEmptyValues(viewSettings.filter || {});
  viewSettings.customParams = withoutEmptyValues(viewSettings.customParams || {});
  viewSettings.dataSource = dataSource;
  viewSettings.rootViewId = rootViewId;
  viewSettings.viewId = getViewIdWithState({
    rootViewId,
    viewSettings,
  });
  viewSettings.loadAllData = !!viewSettings.loadAllData;
  viewSettings.includeItemsCount = viewSettings.includeItemsCount || false;

  state.set(['dataTables', dataSource, 'viewSettings', rootViewId], viewSettings);

  return actionInitializeDataTableState({ state }, {
    dataSource,
    filter: viewSettings.filter,
    sort: viewSettings.sort,
    fuzzy: viewSettings.fuzzy,
    customParams: viewSettings.customParams,
    viewId: viewSettings.viewId,
    loadAllData: viewSettings.loadAllData,
  });
}

/**
 * Loads the next page of a data table.
 *
 * @param  {Object}   options.state      Baobab state object.
 * @param  {Object}   options.apiClient  API client created via `/api/createApiClient`.
 * @param  {string}   options.dataSource Data source.
 * @param  {string}   options.viewId     View ID (optional).
 * @param  {number}   options.count      Number of items to load.
 * @param  {Function} options.onSuccess  Function to run after the items are loaded.
 * @return {undefined}
 */
export function actionDataTableLoadNextPage({ state, apiClient }, {
  dataSource,
  viewId,
  count,
  onSuccess,
  includeItemsCount,
}) {
  typeCheck('dataSource::NonEmptyString', dataSource);
  typeCheck('viewId::NonEmptyString', viewId);
  typeCheck('count::PositiveNumber', count);
  typeCheck('onSuccess::Maybe Function', onSuccess);

  const dataTableViewConfig = state.get(['dataTables', dataSource, 'views', viewId]);
  if (!dataTableViewConfig) {
    const error = new Error(`DataTable "${dataSource}" view ID "${viewId}" hasn't been configured yet.`);
    error.name = 'SrDataTableViewNotConfiguredError';
    error.details = {
      dataSource,
      viewId,
      count,
    };
    throw error;
  }

  const {
    filter,
    sort,
    fuzzy,
    customParams,
    loadedCount,
  } = dataTableViewConfig;

  const skip = loadedCount;

  if (!dataTableViewConfig.isFullyLoaded) {
    return actionDataTableLoadItems({ state, apiClient }, {
      dataSource,
      viewId,
      count,
      onSuccess,
      filter,
      sort,
      fuzzy,
      skip,
      customParams,
      // Only including the items count on the first data load
      // TODO: helper/action to reload the counts only (or force a count reload on getItems
      includeItemsCount: includeItemsCount && !loadedCount,
    });
  }
}

/**
 * Load data table items and store them in the provided state object.
 *
 * @param  {Object} options.state        Baobab state object.
 * @param  {Object} options.apiClient    API client created via `/api/createApiClient`.
 * @param  {string} options.dataSource   Data source.
 * @param  {string} options.viewId       View ID (optional).
 * @param  {Object} options.filter       Filter object (optional).
 * @param  {Object} options.sort         Sort object (optional).
 * @param  {Object} options.fuzzy        Fuzzy object (optional).
 * @param  {number} options.skip         Number of items to skip.
 * @param  {Object} options.customParams Custom parameters to be sent to the data table's handler query generator.
 * @param  {number} options.count        Number of items to load.
 * @return {undefined}
 */
export async function actionDataTableLoadItems({ state, apiClient }, args) {
  const {
    dataSource,
    viewId,
    filter,
    sort,
    fuzzy,
    skip,
    count,
    customParams,
    onSuccess,
    includeItemsCount,
  } = args;
  typeCheck('dataSource::NonEmptyString', dataSource);
  typeCheck('viewId::Maybe NonEmptyString', viewId);
  typeCheck('filter::Maybe Object', filter);
  typeCheck('sort::Maybe Object', sort);
  typeCheck('fuzzy::Maybe Object', fuzzy);
  typeCheck('skip::Maybe Number', skip);
  typeCheck('count::PositiveNumber', count);
  typeCheck('onSuccess::Maybe Function', onSuccess);
  typeCheck('includeItemsCount::Maybe Boolean', includeItemsCount);
  const sortId = makeSortIdString(sort);

  const result = await apiClient.postWithState({
    action: SR_API_ACTION_GET_DATA_TABLE_ITEMS,
    queryId: lodashCompact([dataSource, viewId]).join('-'),
    state: state,
    payload: {
      dataSource,
      filter,
      sort,
      fuzzy,
      skip,
      count,
      customParams,
      includeItemsCount,
    },
    onSuccess({ items, totalItemsCount, isFullyLoaded }) {
      const itemsById = items.reduce((acc, item) => ({
        ...acc,
        [item.id]: item,
      }), {});

      const updatedItemIds = [];

      // Update existing items.
      state.get(['dataTables', dataSource, 'items']).forEach((currItem) => {
        const newItem = itemsById[currItem.id];
        if (newItem) {
          state.set(['dataTables', dataSource, 'items', { id: newItem.id }], newItem);
          updatedItemIds.push(newItem.id);
        }
      });

      // Add new items to sort group.
      const itemIdsInSortGroup = state.get(['dataTables', dataSource, 'itemIdsBySort', sortId]) || [];
      const itemIdsExistInSortGroup = itemIdsInSortGroup
        .reduce((memo, id) => ({
          ...memo,
          [id]: true,
        }), {});
      for (const item of items) {
        if (!itemIdsExistInSortGroup[item.id]) {
          state.push(['dataTables', dataSource, 'itemIdsBySort', sortId], item.id);
        }
      }

      // Add new items.
      const itemsToAdd = items.filter(({ id }) => {
        return !updatedItemIds.includes(id);
      });
      state.concat(['dataTables', dataSource, 'items'], itemsToAdd);
      if (viewId) {
        state.set(['dataTables', dataSource, 'views', viewId, 'isFullyLoaded'], isFullyLoaded || false);
        if (typeof totalItemsCount === 'number') {
          state.set(['dataTables', dataSource, 'views', viewId, 'totalItemsCount'], totalItemsCount);
        }
      }

      // Update the view's loaded items count.
      state.apply(
        ['dataTables', dataSource, 'views', viewId, 'loadedCount'],
        (loadedCount) => loadedCount + count,
      );

      debug('actionDataTableLoadItems onSuccess', {
        itemsById,
        updatedItemIds,
        itemsToAdd,
        viewId,
        isFullyLoaded,
      });

      if (onSuccess) {
        onSuccess({ items, isFullyLoaded });
      }
    },
  });

  return result;
}

/**
 * Load data table items and store them in the provided state object,
 * only if the data table's view is not fully loaded.
 *
 * @param  {Object} options.state        Baobab state object.
 * @param  {Object} options.apiClient    API client created via `/api/createApiClient`.
 * @param  {string} options.dataSource   Data source.
 * @param  {string} options.viewId       View ID (optional).
 * @param  {Object} ...options.args      Remaining args for actionDataTableLoadItems.
 * @return {undefined}
 */
export function actionDataTableLoadItemsIfNotFullyLoaded({ state, apiClient }, {
  dataSource,
  viewId,
  ...args
}) {
  typeCheck('dataSource::NonEmptyString', dataSource);
  typeCheck('viewId::Maybe NonEmptyString', viewId);

  const dataTableIsFullyLoaded = state.get([
    'dataTables',
    dataSource,
    'views',
    viewId,
    'isFullyLoaded',
  ]);

  if (!dataTableIsFullyLoaded) {
    return actionDataTableLoadItems({ state, apiClient }, {
      dataSource,
      viewId,
      ...args,
    });
  }
}

export async function actionDataTableReset({ state }, { dataSource }) {
  typeCheck('dataSource::NonEmptyString', dataSource);
  state.unset(['dataTables', dataSource]);
  state.commit();
}

export async function actionDataTableRemoveItem({ state }, { dataSource, entityId }) {
  typeCheck('dataSource::NonEmptyString', dataSource);
  typeCheck('entityId::PositiveNumber|NonEmptyString', entityId);

  const dataTableItems = state.get(['dataTables', dataSource, 'items']);

  if (Array.isArray(dataTableItems)) {
    state.set(['dataTables', dataSource, 'items'], dataTableItems.filter((item) => {
      return item.id !== entityId;
    }));
  }
}

/**
 * Gets a data table's settings from a getter function and a props object.
 *
 * @param  {Function|Object} dataTableParamsOrGetter Decorator params object, or getter function.
 * @param  {Object}          props                   React component's props.
 * @return {Object}          Data table settings object.
 */
function _dataTableGetParamsFromProps(dataTableParamsOrGetter, props) {
  const params = typeof dataTableParamsOrGetter === 'function'
    ? dataTableParamsOrGetter(props)
    : dataTableParamsOrGetter;

  if (!params) {
    return {
      skipInitialize: true,
    };
  }

  typeCheck('params::NonEmptyObject', params);
  typeCheck('dataSource::NonEmptyString', params.dataSource);
  typeCheck('viewId::NonEmptyString', params.viewId);
  return params;
}

/**
 * Gets the data table cursors required by the state manager.
 *
 * @param   {Function/Object} dataTableParamsOrGetter Decorator params object, or getter function.
 * @returns {Object}          Data table cursors.
 */
export function dataTableGetBranchParams(dataTableParamsOrGetter) {
  return (props) => {
    const params = _dataTableGetParamsFromProps(dataTableParamsOrGetter, props);

    if (!params.dataSource || !params.viewId) {
      return {};
    }
    return {
      items: ['dataTables', params.dataSource, 'views', params.viewId, '$items'],
      totalItemsCount: ['dataTables', params.dataSource, 'views', params.viewId, 'totalItemsCount'],
      dataTableIsFullyLoaded: ['dataTables', params.dataSource, 'views', params.viewId, 'isFullyLoaded'],
    };
  };
}

/**
 * Data table React HOC decorator.
 *
 * @param  {Function|Object} dataTableParamsOrGetter Decorator params object, or getter function.
 * @return {Function} React HOC decorator.
 */
export function dataTable(dataTableParamsOrGetter) {
  typeCheck(
    'dataTableParamsOrGetter::Function | NonEmptyObject',
    dataTableParamsOrGetter,
  );

  return function dataTableDecorator(ComponentToDecorate) {
    @withApiState((props) => {
      const params = _dataTableGetParamsFromProps(dataTableParamsOrGetter, props);
      if (!params.dataSource || !params.viewId) {
        return {};
      }
      return {
        dataTableApiState: {
          action: SR_API_ACTION_GET_DATA_TABLE_ITEMS,
          queryId: lodashCompact([params.dataSource, params.viewId]).join('-'),
        },
      };
    })
    @branch(dataTableGetBranchParams(dataTableParamsOrGetter))
    class WithDataTable extends Component {
      static propTypes = {
        dispatch: PropTypes.func.isRequired,
      }

      UNSAFE_componentWillMount() {
        const { dispatch } = this.props;
        const params = _dataTableGetParamsFromProps(dataTableParamsOrGetter, this.props);
        if (!params.skipInitialize) {
          dispatch(actionInitializeDataTableState, params);
        }
      }

      UNSAFE_componentWillReceiveProps(props) {
        const { dispatch } = this.props;
        const params = _dataTableGetParamsFromProps(dataTableParamsOrGetter, props);
        if (!params.skipInitialize) {
          dispatch(actionInitializeDataTableState, params);
        }
      }

      render() {
        const { items, totalItemsCount, dataTableApiState, dataTableIsFullyLoaded } = this.props;
        const params = _dataTableGetParamsFromProps(dataTableParamsOrGetter, this.props);
        const itemsPropName = params.itemsPropName || 'items';
        const itemsToInject = params.injectSingleItem
          ? (items || [])[0]
          : items || [];

        const childProps = {
          ...this.props,
          [itemsPropName]: itemsToInject,
          [`${itemsPropName}Count`]: totalItemsCount,
          dataTableIsFullyLoaded: dataTableIsFullyLoaded || false,
          dataTableApiState: dataTableApiState || {},
        };

        if (params.dataSource) {
          childProps.dataSource = params.dataSource;
        }
        if (params.viewId) {
          childProps.viewId = params.viewId;
        }

        return (
          <ComponentToDecorate {...childProps} />
        );
      }
    }

    configureDecoratedComponent({
      DecoratedComponent: WithDataTable,
      OriginalComponent: ComponentToDecorate,
      displayNameSuffix: 'WithDataTable',
    });

    return WithDataTable;
  };
}

/**
 * Data table controller with automated filtering/sorting/loading behaviors.
 *
 * @param  {Function|Object} dataTableParamsOrGetter Decorator params object, or getter function.
 * @return {Function} React HOC decorator.
 */
export function dataTableUIControls(dataTableParamsOrGetter) {
  dataTableParamsOrGetter = typeof dataTableParamsOrGetter === 'function'
    ? dataTableParamsOrGetter
    : () => dataTableParamsOrGetter;
  return function dataTableUIControlsDecorator(ComponentToDecorate) {
    @branch({
      currentUser: ['currentUser'],
    })
    @branch((props) => {
      const params = dataTableParamsOrGetter(props);
      if (!params.dataSource || !params.rootViewId) {
        return {};
      }
      return {
        viewSettings: ['dataTables', params.dataSource, 'viewSettings', params.rootViewId],
      };
    })
    @dataTable((props) => {
      const {
        viewSettings,
      } = props;
      const {
        dataSource,
        itemsPropName,
        count,
      } = dataTableParamsOrGetter(props);
      if (!viewSettings) {
        return null;
      }
      return {
        viewId: viewSettings.viewId,
        sort: viewSettings.sort,
        fuzzy: viewSettings.fuzzy,
        filter: viewSettings.filter,
        count: viewSettings.loadAllData ? ALL_ITEMS_COUNT : count,
        loadAllData: viewSettings.loadAllData,
        customParams: viewSettings.customParams,
        itemsPropName,
        dataSource,
      };
    })
    @withInfiniteScroll({
      offset: 420,
    })
    class WithDataTableUIControls extends Component {
      static propTypes = {
        dispatch: PropTypes.func.isRequired,
        itemsPropName: PropTypes.string,
        dataSource: PropTypes.string.isRequired,
        rootViewId: PropTypes.string.isRequired,
        viewSettings: PropTypes.object,
        initialViewSettings: PropTypes.object.isRequired,
      }

      static defaultProps = {
        itemsPropName: 'items',
      }

      constructor(props) {
        super(props);
        props.onInfiniteScrollCallbackReady(this._loadNextPage);
      }

      componentDidMount() {
        const { initialViewSettings, viewSettings } = this.props;
        if (initialViewSettings.loadAllData && viewSettings) {
          this._loadNextPage();
        }
      }

      UNSAFE_componentWillMount() {
        const { initialViewSettings } = this.props;
        this._updateViewSettings(initialViewSettings);
      }

      UNSAFE_componentWillReceiveProps(nextProps) {
        const { initialViewSettings, viewSettings } = this.props;

        if (
          initialViewSettings &&
          initialViewSettings !== nextProps.initialViewSettings
        ) {
          this._updateViewSettings(initialViewSettings);
        }

        if (initialViewSettings.loadAllData && ((!viewSettings && nextProps.viewSettings) || viewSettings)) {
          this._loadNextPage(nextProps);
        }
      }

      _loadNextPage = (nextProps) => {
        const {
          viewSettings,
          dataTableIsFullyLoaded,
          dispatch,
        } = (nextProps || this.props);

        const { count } = dataTableParamsOrGetter(this.props);
        if (!dataTableIsFullyLoaded && viewSettings) {
          dispatch(actionDataTableLoadNextPage, {
            dataSource: viewSettings.dataSource,
            viewId: viewSettings.viewId,
            count: viewSettings.loadAllData ? ALL_ITEMS_COUNT : count,
            includeItemsCount: viewSettings.includeItemsCount,
          });
        }
      }

      _updateViewSettings = (viewSettings = {}) => {
        const {
          initialViewSettings,
          dataSource,
          rootViewId,
          dispatch,
        } = this.props;

        dispatch(actionInitializeDataTableUIControlsState, {
          dataSource,
          rootViewId,
          viewSettings: {
            ...viewSettings,
            loadAllData: initialViewSettings.loadAllData,
          },
        });
      }

      _toggleSort = (fieldId) => {
        const {
          initialViewSettings,
          viewSettings,
          sortUserSettingsFieldId,
          currentUser,
          dispatch,
        } = this.props;
        let newSortDirection;
        switch (viewSettings.sort[fieldId]) {
          case 'desc': newSortDirection = 'asc'; break;
          case 'asc': newSortDirection = null; break;
          default: newSortDirection = 'desc';
        }
        if (!newSortDirection) {
          this._updateViewSettings({
            ...initialViewSettings,
          });
        }
        else {
          this._updateViewSettings({
            ...initialViewSettings,
            sort: {
              [fieldId]: newSortDirection,
            },
          });
        }
        if (sortUserSettingsFieldId) {
          // eslint-disable-next-line max-len
          const newUserSettings = setIn({ ...currentUser.settings }, sortUserSettingsFieldId,
            newSortDirection ? { [fieldId]: newSortDirection } : undefined,
          );
          dispatch(updateSettings, newUserSettings);
        }
      }

      _reloadItems = ({ count, filter, customParams }) => {
        const {
          dispatch,
          dataSource,
          rootViewId,
          viewSettings,
        } = this.props;
        return dispatch(actionDataTableLoadItems, {
          dataSource: dataSource,
          viewId: rootViewId,
          count,
          customParams: customParams,
          filter: {
            ...(viewSettings.filter || {}),
            ...filter,
          },
          sort: viewSettings.sort,
        });
      }

      render() {
        const {
          items,
          totalItemsCount,
          itemsPropName,
          viewSettings,
          dispatch,
          dataTableIsFullyLoaded,
          onInfiniteScrollWrapperRef,
        } = this.props;

        return (
          <ComponentToDecorate
            {...this.props}
            {...{ [itemsPropName]: items }}
            {...{ [`${itemsPropName}Count`]: totalItemsCount }}
            dispatch={dispatch}
            reloadItems={this._reloadItems}
            dataTableIsFullyLoaded={dataTableIsFullyLoaded || false}
            viewSettings={viewSettings && {
              update: this._updateViewSettings,
              toggleSort: this._toggleSort,
              ...viewSettings,
            }}
            onInfiniteScrollWrapperRef={onInfiniteScrollWrapperRef}
          />
        );
      }
    }

    configureDecoratedComponent({
      DecoratedComponent: WithDataTableUIControls,
      OriginalComponent: ComponentToDecorate,
      displayNameSuffix: 'WithDataTableUIControls',
    });

    return WithDataTableUIControls;
  };
}
